// MainStore
import { MainStore } from "./../stores/MainStore";

// Framework
import { User } from "../framework/user/User";
import { UserProfile_e } from "../framework/user/UserProfile";
import { sha256 } from 'js-sha256';

/**
 * @description Contain all the method type of an RESTful API
 * @author Lucas Fridez <lucas@fridez.dev>
 * @export
 * @enum {number}
 */
export enum MethodRequest_e {
    GET = "get",
    POST = "post",
    PUT = "put",
    DELETE = "delete"
}

/**
 * @description NetworkManager that contain all the function about the API
 * @author Lucas Fridez <lucas@fridez.dev>
 * @export
 * @class NetworkManager
 */

export class NetworkManager {

    public static IMG_URL = window.location.hostname == "localhost" ? "http://localhost:8000/img" : "https://www.api.sotramelan.ch/img";

    /**
     * @description COnnect the User with the API
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @param {string} [username]
     * @param {string} [password]
     * @returns {Promise<void>}
     * @memberof NetworkManager
     */
    public static async connect(username?: string, password?: any, rememberMe?: boolean) {
        try {
            // If no username or password is provided, get the localStorage data
            if (username === undefined && password === undefined) {
                const token = JSON.parse(window.localStorage.getItem("userCredentials") || window.sessionStorage.getItem("userCredentials") || "");
                if (token != null) {
                    this.userConnected = new User(token);
                    MainStore.loginUser(this.userConnected);
                    return;
                }
            } else {
                const request = await this.request("login", MethodRequest_e.POST, {
                    password: sha256(password),
                    right: 2,
                    username: username
                });

                //Return status of connection
                if (request.error) {
                    return request;
                } else {
                    this.userConnected = new User(request);
                    MainStore.loginUser(this.userConnected);
                    if (rememberMe) {
                        window.localStorage.setItem("userCredentials", JSON.stringify(request));
                    } else {
                        window.sessionStorage.setItem("userCredentials", JSON.stringify(request));
                    }
                    return true;
                }
            }
        } catch (err) {
            MainStore.loginUserError();
            throw (err);
        }
    }

    /**
     * @description Disconnect the actual User
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @memberof NetworkManager
     */
    public static disconnect(): void {
        window.localStorage.removeItem("userCredentials");
        this.userConnected = undefined;
        MainStore.logoutUser();
    }

    /**
     * @description Show a Network Error
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @memberof NetworkManager
     */
    /*public static showError(message: string) {
        //TODO: Add Snackbar Error
    }*/

    /**
     * @description Request any route to the API
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @param {string} route
     * @param {MethodRequest_e} method
     * @param {*} [body]
     * @returns {Promise<any>}
     * @memberof NetworkManager
     */
    public static async request(route: string, method: MethodRequest_e, body?: any): Promise<any> {
        try {
            let headers = { "Content-Type": "application/json" };
            if (method != MethodRequest_e.GET && route != "login") {
                headers = Object.assign({ "UserId": this.getUserId() }, headers);
            }

            //Send request
            const res = await fetch(`${this.API_SERVER}/${route}`, {
                body: JSON.stringify(body),
                headers: headers,
                method: method
            });

            // Return response
            if (res.status == 200) {
                return JSON.parse(await res.text());
                // Send error
            } else if (((res.status != 204) as boolean) && ((res.status != 201) as boolean)) {
                throw (JSON.parse(await res.text()));
            }
        } catch (error) {
            throw(error);
        }

    }

    /**
     * @description Get the user credentials stocked in the localStorage
     * @author Lucas Fridez <lucas@fridez.dev>
     * @private
     * @returns {Array<any>} the user credentials
     * @memberof NetworkManager
     */
    public static getUserCredentials() {
        if (this.userConnected === undefined) {
            try {
                const credentials = JSON.parse(window.localStorage.getItem("userCredentials") || window.sessionStorage.getItem("userCredentials") || "");
                this.userConnected = new User(credentials);
            } catch (error) {
                return undefined;
            }
        }
        return this.userConnected.username;
    }

    /**
     * @description Get the User id
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @returns {string} user id
     * @memberof NetworkManager
     */
    public static getUserId(): string | undefined {
        return this.userConnected ? this.userConnected.id : undefined;
    }

    /**
     * @description Get the username of the NetworkManager
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @returns {string} username
     * @memberof NetworkManager
     */
    public static getUsername(): string | undefined {
        return this.userConnected ? this.userConnected.username : undefined;
    }

    /**
     * @description Get the API Server Domain
     * @author Lucas Fridez <lucas@fridez.dev>
     * @static
     * @returns {string} api server domain
     * @memberof NetworkManager
     */
    public static getApiServer(): string {
        return this.API_SERVER;
    }

    public static getUserProfile(): UserProfile_e | undefined {
        return this.userConnected ? this.userConnected.profile : undefined;
    }

    private static userConnected: User | undefined = undefined;

    private static API_SERVER = window.location.hostname == "localhost" ? "http://localhost:8000" : "https://api.sotramelan.ch";
}



// WEBPACK FOOTER //
// ./src/app/managers/NetworkManager.ts